import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

export type AnimatedNumberProps = {
  value: number;
  durationMs?: number;
  format?: (n: number) => string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
};

/**
 * Count-up on inView (IntersectionObserver, threshold 0.4, once).
 * Renders in JetBrains Mono tabular-nums (.num) so digits never
 * reflow mid-count. Supports yen/percent/decimal formatting.
 *
 * Under prefers-reduced-motion it renders the final value immediately.
 * Always settles last in its group (figure tier ~1.2-1.6s).
 */
export function AnimatedNumber({
  value,
  durationMs = 1400,
  format,
  prefix = '',
  suffix = '',
  decimals = 0,
  className,
}: AnimatedNumberProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLSpanElement | null>(null);
  const started = useRef(false);
  const [display, setDisplay] = useState(reduce ? value : 0);

  useEffect(() => {
    if (reduce) {
      setDisplay(value);
      return;
    }
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now: number) => {
              const t = Math.min(1, (now - start) / durationMs);
              const eased = 1 - Math.pow(1 - t, 3);
              setDisplay(value * eased);
              if (t < 1) raf = requestAnimationFrame(tick);
              else setDisplay(value);
            };
            raf = requestAnimationFrame(tick);
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value, durationMs, reduce]);

  const rendered = format
    ? format(display)
    : `${prefix}${display.toFixed(decimals)}${suffix}`;

  return (
    <span ref={ref} className={`num ${className ?? ''}`.trim()}>
      {rendered}
    </span>
  );
}
